import { getSupabaseClient } from '../lib/supabase';
import { TimeSlot, AvailabilityDay, AvailabilityResponse } from '../types';
import { isSlotInPast } from '../utils/dateUtils';

export interface ScheduledSession {
  id: string;
  guide_id: string;
  seeker_id?: string;
  session_type: 'consultation' | 'preparation' | 'integration' | 'journey' | 'follow_up';
  session_date: string; // Legacy: YYYY-MM-DD format
  session_time: string; // Legacy: HH:MM format
  duration_minutes: number;
  session_status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  notes?: string;
  seeker_name?: string;
  seeker_email?: string;
  seeker_phone?: string;
  guide_name?: string;
  location?: string;
  location_details?: string | null;
  created_at: string;
  updated_at: string;
  // Timezone-aware fields (source of truth for display)
  start_time?: string | null; // UTC timestamptz
  end_time?: string | null; // UTC timestamptz
  guide_timezone?: string | null; // IANA timezone
  seeker_timezone?: string | null; // IANA timezone
}

export interface AvailabilityDayWithSlots extends AvailabilityDay {
  dayOfWeek: string;
}

export class SchedulingService {
  /**
   * Get guide's availability for the next 30 days, including Google Calendar integration
   * Returns timezone-aware slots when available
   *
   * IMPORTANT: The guide-availability edge function is the source of truth for working hours.
   * It reads from the guide_working_hours table and respects custom schedules.
   * The legacy fallback method uses hardcoded 9-5 hours and should only be used when
   * the edge function is unavailable.
   */
  static async getGuideAvailability(guideId: string, daysAhead = 30): Promise<AvailabilityDayWithSlots[]> {
    try {
      // Try to use the new integrated availability endpoint first
      const env = (window as any).__ENV__;
      const baseUrl = env?.SUPABASE_URL;

      if (baseUrl) {
        const today = new Date();
        const startDate = today.toISOString().split('T')[0];
        const endDate = new Date(today.getTime() + daysAhead * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

        const response = await fetch(
          `${baseUrl}/functions/v1/guide-availability?guide_id=${guideId}&start_date=${startDate}&end_date=${endDate}`
        );

        if (response.ok) {
          const data: AvailabilityResponse = await response.json();

          // Check if we have timezone-aware data
          const hasTimezoneData = data.timezone_version && data.guide_timezone;

          // Filter out past slots
          const filteredAvailability = data.availability.map((day) => {
            const filteredSlots = day.timeSlots.filter((slot) => {
              return !isSlotInPast(slot.start_utc, day.date, slot.time);
            });

            return {
              ...day,
              timeSlots: filteredSlots,
              dayOfWeek: new Date(day.date).toLocaleDateString('en-US', { weekday: 'long' }),
            };
          });

          console.log('Using timezone-aware availability:', hasTimezoneData);
          return filteredAvailability;
        }
      }
    } catch (error) {
      console.warn('Failed to fetch integrated availability, falling back to legacy method:', error);
    }

    // Fallback to legacy method (direct database query without calendar integration)
    return this.getGuideAvailabilityLegacy(guideId, daysAhead);
  }

  /**
   * Legacy method: Get guide's availability from database only (no calendar integration)
   */
  private static async getGuideAvailabilityLegacy(guideId: string, daysAhead = 30): Promise<AvailabilityDayWithSlots[]> {
    const supabase = getSupabaseClient();

    // Generate date range for the next 30 days
    const dates = this.generateDateRange(daysAhead);

    // Get existing bookings for this guide
    const { data: existingBookings, error } = await supabase
      .from('scheduled_sessions')
      .select('session_date, session_time, duration_minutes')
      .eq('guide_id', guideId)
      .in('session_status', ['scheduled', 'confirmed'])
      .gte('session_date', dates[0].date)
      .lte('session_date', dates[dates.length - 1].date);

    if (error) {
      throw new Error(`Failed to fetch existing bookings: ${error.message}`);
    }

    // Create availability for each day
    const availability: AvailabilityDayWithSlots[] = dates.map(date => {
      const dayBookings = (existingBookings || []).filter((booking: any) => booking.session_date === date.date);
      const timeSlots = this.generateTimeSlots(date.date, dayBookings);

      return {
        date: date.date,
        dayOfWeek: date.dayOfWeek,
        timeSlots
      };
    });

    return availability;
  }

  /**
   * Generate array of dates for the next N days
   */
  private static generateDateRange(daysAhead: number): Array<{ date: string; dayOfWeek: string }> {
    const dates = [] as Array<{ date: string; dayOfWeek: string }>;
    const today = new Date();

    for (let i = 0; i <= daysAhead; i++) {
      const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);

      const dayOfWeekIndex = date.getDay();
      if (dayOfWeekIndex === 0 || dayOfWeekIndex === 6) {
        continue;
      }

      const isoDate = `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-${`${date.getDate()}`.padStart(2, '0')}`;
      const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });

      dates.push({
        date: isoDate,
        dayOfWeek,
      });
    }

    return dates;
  }

  /**
   * Generate available time slots for a day, excluding existing bookings
   * Note: This is a legacy fallback - the server-side guide-availability endpoint
   * is the source of truth for working hours and should be used when possible.
   */
  private static generateTimeSlots(
    date: string,
    existingBookings: any[],
    startHour = 9,
    endHour = 17
  ): TimeSlot[] {
    // Generate business hours based on provided start/end (defaults to 9 AM - 5 PM)
    const businessHours: string[] = [];
    for (let hour = startHour; hour <= endHour; hour++) {
      for (const minute of [0, 30]) {
        if (hour === endHour && minute === 30) {
          // Last slot ends at end hour + 30
          businessHours.push(`${`${hour}`.padStart(2, '0')}:${`${minute}`.padStart(2, '0')}`);
          break;
        }
        businessHours.push(`${`${hour}`.padStart(2, '0')}:${`${minute}`.padStart(2, '0')}`);
      }
    }

    const bookedTimes = existingBookings
      .map((booking: any) => {
        if (!booking?.session_time) return null;
        const timeStr = String(booking.session_time);
        return timeStr.includes(':') ? timeStr.slice(0, 5) : timeStr;
      })
      .filter((value): value is string => Boolean(value));

    return businessHours.map(time => {
      const isBooked = bookedTimes.includes(time);
      const isPast = isSlotInPast(undefined, date, time);

      return {
        time,
        available: !isBooked && !isPast
      };
    });
  }

  /**
   * Get seeker's upcoming sessions
   */
  static async getSeekerSessions(seekerEmail: string): Promise<ScheduledSession[]> {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
      .from('scheduled_sessions')
      .select('*')
      .eq('seeker_email', seekerEmail)
      .gte('session_date', new Date().toISOString().split('T')[0])
      .order('session_date', { ascending: true })
      .order('session_time', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch seeker sessions: ${error.message}`);
    }

    return data as ScheduledSession[];
  }
}
